import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface AuthGuardProps {
  children: ReactNode;
  requireGroup?: boolean;
}

/**
 * Route guard that redirects unauthenticated users to login.
 *
 * If requireGroup is true (default), also redirects users without groups
 * to the onboarding flow.
 */
export function AuthGuard({ children, requireGroup = true }: AuthGuardProps) {
  const { isAuthenticated, isLoading, needsOnboarding, activeGroup } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to onboarding if no groups
  if (requireGroup && (needsOnboarding || !activeGroup)) {
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

export default AuthGuard;
